import React from "react";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Plane, Clock, ShieldCheck, ArrowRight } from "lucide-react";

const PREMIUM_IMG =
  "https://customer-assets-v7afamib.emergentagent.net/job_gallura-auto-rental/artifacts/plwxbixm_Screenshot_20260710_021646_Flow.jpg";

const CONTENT = {
  it: {
    eyebrow: "Transfer VIP · Aeroporti & Yacht",
    title: "Il tuo arrivo in Gallura, con la classe che meriti",
    lead:
      "Che tu atterri all'Aeroporto Olbia Costa Smeralda o al terminal aviazione generale, il nostro autista in giacca e cravatta ti accoglie all'uscita con cartello personalizzato. Ideale per business travel, coppie in luna di miele, celebrità e ospiti di ville private.",
    p1_title: "Attesa gratuita",
    p1_desc: "Monitoraggio volo in tempo reale, fino a 60 minuti di attesa senza costi.",
    p2_title: "Discrezione totale",
    p2_desc: "NDA firmabile su richiesta, autista formato per clientela VIP e riservata.",
    p3_title: "Aviazione privata",
    p3_desc: "Accesso al terminal jet privati di Olbia e coordinamento con equipaggio.",
    cta: "Prenota un transfer VIP",
    tag: "Aeroporto Olbia · Costa Smeralda",
  },
  en: {
    eyebrow: "VIP Transfer · Airports & Yachts",
    title: "Arrive in Gallura with the class you deserve",
    lead:
      "Whether you land at Olbia Costa Smeralda Airport or the private aviation terminal, our suited driver welcomes you at arrivals with a personalised name sign. Ideal for business travel, honeymoons, celebrities and private villa guests.",
    p1_title: "Free waiting",
    p1_desc: "Real-time flight tracking, up to 60 minutes waiting at no extra cost.",
    p2_title: "Total discretion",
    p2_desc: "NDA available on request, driver trained for VIP and confidential clientele.",
    p3_title: "Private aviation",
    p3_desc: "Access to Olbia's private jet terminal and crew coordination.",
    cta: "Book a VIP transfer",
    tag: "Olbia Airport · Costa Smeralda",
  },
};

export default function PremiumTransferSection() {
  const { lang } = useI18n();
  const c = CONTENT[lang] || CONTENT.it;

  return (
    <section
      data-testid="premium-transfer-section"
      className="bg-[#F9F7F3] py-20 lg:py-28"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        {/* Image column */}
        <div className="relative order-2 lg:order-1">
          <div className="absolute -top-4 -left-4 w-24 h-24 accent-bg rounded-full opacity-90 hidden md:block" />
          <div className="absolute -bottom-4 -right-4 w-32 h-32 brand-bg rounded-full opacity-90 hidden md:block" />
          <div className="relative rounded-2xl overflow-hidden shadow-2xl">
            <img
              src={PREMIUM_IMG}
              alt={
                lang === "it"
                  ? "Autista privato Gallura Drive con jet privato in aeroporto — Transfer VIP"
                  : "Gallura Drive private chauffeur with private jet at airport — VIP transfer"
              }
              className="w-full h-auto object-cover aspect-[16/10]"
              loading="lazy"
            />
            {/* Tag overlay */}
            <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm rounded-full px-4 py-2 flex items-center gap-2 shadow-lg">
              <Plane className="w-4 h-4 brand-text" />
              <span className="text-xs font-medium text-slate-800 tracking-wide">
                {c.tag}
              </span>
            </div>
          </div>
        </div>

        {/* Content column */}
        <div className="order-1 lg:order-2">
          <div className="uppercase tracking-[0.3em] text-xs text-slate-500 mb-4">
            {c.eyebrow}
          </div>
          <h2 className="font-display text-4xl md:text-5xl brand-text leading-tight">
            {c.title}
          </h2>
          <p className="mt-6 text-slate-700 leading-relaxed text-base md:text-lg max-w-xl">
            {c.lead}
          </p>

          {/* Feature list */}
          <div className="mt-10 space-y-5">
            {[
              { icon: <Clock className="w-5 h-5" />, title: c.p1_title, desc: c.p1_desc },
              { icon: <ShieldCheck className="w-5 h-5" />, title: c.p2_title, desc: c.p2_desc },
              { icon: <Plane className="w-5 h-5" />, title: c.p3_title, desc: c.p3_desc },
            ].map((f) => (
              <div
                key={f.title}
                data-testid={`premium-feature-${f.title.slice(0, 20)}`}
                className="flex gap-4 items-start"
              >
                <div className="w-11 h-11 shrink-0 rounded-full brand-bg text-white grid place-items-center">
                  {f.icon}
                </div>
                <div>
                  <div className="font-display text-lg brand-text">{f.title}</div>
                  <div className="text-sm text-slate-600 leading-relaxed mt-1">
                    {f.desc}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10">
            <Button
              data-testid="premium-cta-book"
              onClick={() =>
                document.getElementById("booking")?.scrollIntoView({ behavior: "smooth" })
              }
              className="brand-bg hover:opacity-90 text-white rounded-full px-7 py-6 text-base"
            >
              {c.cta}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
